import { useEffect, useState } from 'react';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { Loading } from '@bumble/app/components/Loading';
import { IOSSwitch } from '@bumble/app/components/IOSSwitch';

interface RouterSecondaryWanEditProps {
  onSubmit: () => void;
  cpeId: string;
}

export const RouterSecondaryWanEdit = (
  { onSubmit, cpeId }: RouterSecondaryWanEditProps
) => {
  const api = useApi();
  const [enableSecondaryWan, setEnableSecondaryWan] = useState<boolean>(false);
  const [newDhcpClient, setNewDhcpClient] = useState<boolean>(false);
  const [newCidr, setNewCidr] = useState<string>('');
  const [newGwIp, setNewGwIp] = useState<string>('');
  const [newWanDnsIp, setNewWanDnsIp] = useState<string>('');
  const [staticIp, setStaticIp] = useState<boolean>(false);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [resultMessage, setResultMessage] = useState<string>('');

  const { data: wanData, isLoading: wanLoading } = useQuery(
    ['wanConfig'],
    api.getSecondaryWanConfigApiV1CpeGetSecondaryWanConfigGet.bind(null, {
      cpe_id: cpeId
    }),
    {enabled: true, refetchOnMount: true}
  )

  useEffect(() => {
    if (wanData) {
      setEnableSecondaryWan((wanData.data as any).enabled);
      setNewDhcpClient((wanData.data as any).dhcp);
      setNewCidr((wanData.data as any).wan_cidr_addr ?? '');
      setNewGwIp((wanData.data as any).wan_default_gw_ip ?? '');
      setNewWanDnsIp((wanData.data as any).wan_dns_ip ?? '');
      setStaticIp((wanData.data as any).static_ip ?? false);
    }
  }, [wanData]);

  const cpeSecondaryWanUpdatePost = useMutation(api.cpeConfigureSecondaryWanApiV1CpeConfigureSecondaryWanPost, {
    onSuccess: (res: any) => {
      if (res.status === 202) {
        setResultMessage(res.data.detail);
        setShowConfirmModal(true);
      } else {
        setShowConfirmModal(false);
        toast.success('Updated network interface successfully.');
        onSubmit();
      }
    },
    onError: apiError,
  });

  const handleSubmit = () => {
    cpeSecondaryWanUpdatePost.mutate({
      cpe_id: cpeId,
      enabled: enableSecondaryWan,
      wan_dhcp_c: newDhcpClient,
      wan_cidr_addr: newCidr,
      wan_default_gw_ip: newGwIp,
      wan_dns_ip: newWanDnsIp,
      static_ip: enableSecondaryWan && staticIp,
    })
  };

  const handleConfirm = () => {
    cpeSecondaryWanUpdatePost.mutate({
      cpe_id: cpeId,
      enabled: enableSecondaryWan,
      wan_dhcp_c: newDhcpClient,
      wan_cidr_addr: newCidr,
      wan_default_gw_ip: newGwIp,
      wan_dns_ip: newWanDnsIp,
      static_ip: enableSecondaryWan && staticIp,
      skip_default_gw_ping: true,
    });
    setShowConfirmModal(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEnableSecondaryWan(e.target.checked);
  };

  return (
    <Box pt={2}>
      {wanLoading ? <Loading /> : (
        <>
          <Box display="flex" justifyContent="start" alignItems="center" sx={{ mb: 4 }}>
            <FormControlLabel
              value="top"
              control={
                <IOSSwitch color="primary" checked={enableSecondaryWan} onChange={handleChange} />
              }
              label=''
              labelPlacement="bottom"
              sx={(theme) => ({ '&:hover': { color: theme.palette.primary.main } })}
              componentsProps={{
                typography: {
                  sx: (theme) => ({
                    fontSize: 12,
                    color: !enableSecondaryWan ? theme.palette.grey[600] : theme.palette.primary.main,
                  }),
                },
              }}
            />
            <Typography>Enable Secondary WAN</Typography>
          </Box>
          <FormControlLabel
            sx={{ mb: 4, ml: 2 }}
            control={
              <Checkbox
                checked={newDhcpClient}
                onChange={(e) => setNewDhcpClient(e.target.checked)}
              />
            }
            label="DHCP Client"
          />
          {!newDhcpClient && <>
            <TextField
              fullWidth
              sx={{ mb: 4 }}
              label="IP Address in CIDR format (Ex: A.B.C.D/24)"
              variant="outlined"
              value={newCidr}
              onChange={(e) => setNewCidr(e.target.value)}
              autoFocus
            />
            <TextField
              fullWidth
              sx={{ mb: 4 }}
              label="Default Gateway IP"
              variant="outlined"
              value={newGwIp}
              onChange={(e) => setNewGwIp(e.target.value)}
            />
            <TextField
              fullWidth
              sx={{ mb: 4 }}
              label="DNS Server IP(s)"
              variant="outlined"
              value={newWanDnsIp}
              onChange={(e) => setNewWanDnsIp(e.target.value)}
            />
          </>}
          {enableSecondaryWan && <Box>
            <FormControlLabel
              sx={{ ml: 2 }}
              control={
                <Checkbox
                  checked={staticIp}
                  onChange={(e) => setStaticIp(e.target.checked)}
                />
              }
              label={
                <>
                  Bumblebee Static IP
                  {staticIp && (wanData?.data as any)?.static_ip_address && (
                    <Typography component="span" color="text.secondary">
                      {' '}({(wanData?.data as any).static_ip_address})
                    </Typography>
                  )}
                </>
              }
            />
          </Box>}
          <Box display="flex" justifyContent="flex-end" sx={{ mt: 5 }}>
            <Button 
              variant="outlined" 
              onClick={handleSubmit} 
              disabled={(
                !newDhcpClient && (!newCidr || !newGwIp || !newWanDnsIp)) || cpeSecondaryWanUpdatePost.isLoading
              }
            >
              Save
            </Button>
          </Box>
          <Dialog open={showConfirmModal} onClose={() => setShowConfirmModal(false)}>
            <DialogTitle sx={{fontSize: 18}}>Confirm Configuration</DialogTitle>

            <DialogContent>
              {resultMessage}
            </DialogContent>

            <DialogActions>
              <Button onClick={() => setShowConfirmModal(false)}>Cancel</Button>

              <Button
                variant="contained"
                onClick={handleConfirm}
                disabled={cpeSecondaryWanUpdatePost.isLoading}
              >
                Confirm
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};
